import { ProductDto, TargetedSubscriberEnum } from "api/product";
import { ReferentialKey } from "api/referential";
import { useAppDispatch } from "app/hooks";
import { EntityDetailsHeader, EntityDetailsHeaderConfig } from "components/display/EntityDetailsHeader";
import { EntityDetailsStat, EntityDetailsStatConfig } from "components/display/EntityDetailsStat";
import { EditIcon } from "components/icons/Icons";
import { NsKey } from "enums/NsKey";
import { Sizes } from "enums/sizes";
import { getMultipleReferentialsByKeys } from "features/referentials/slices/getMultipleReferentialsByKeysThunk";
import { useDynamicTranslation } from "hooks/useDynamicTranslation";
import { usePanel } from "hooks/usePanel";
import { Calendar, FileText, Layers, Package, TrendingUp, UserCheck } from "lucide-react";
import { FC, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { productsAPI } from "services/swagger-api";
import { GenericFunnelsTable } from "../funnels-view/GenericFunnelsTable";
import { ProductForm } from "./form/ProductForm";
import { targetedSubscriberLabels } from "./form/productInputsConfig";
import ProductPlateformes from "./ProductPlateformes";
import { ProductsDocumentHistory } from "./ProductsDocumentHistory";
import { useProductViewContext } from "./ProductViewContext";

export const ProductDetails: FC = () => {
  const { productId } = useParams<{ productId: string }>()
  const [product, setProduct] = useState<ProductDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { plateformeKeys } = useProductViewContext()
  const dispatch = useAppDispatch();
  const { t } = useDynamicTranslation([NsKey.Product]);

  useEffect(() => {
    dispatch(getMultipleReferentialsByKeys([
      ReferentialKey.Plateforme,
    ]));
  }, [dispatch]);


  const fetchProduct = useCallback(async () => {
    if (!productId) return;
    try {
      setIsLoading(true);
      setError(null);
      const productData = await productsAPI.getProductByID({ productId });
      setProduct(productData);
    } catch (err) {
      console.error("Erreur lors du chargement du produit:", err);
      setError("Impossible de charger les détails du produit");
    } finally {
      setIsLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const { openPanel } = usePanel()

  const openAddFunnel = useCallback(() => {
    openPanel({
      title: "Ajouter un tunnel pour ce produit",
      size: Sizes["4xl"]
    },
      <div></div>
    )
  }, [openPanel])

  const openUpdateProduct = useCallback(() => {
    openPanel({
      title: "Modifier le produit",
      size: Sizes["4xl"]
    },
      <ProductForm productId={productId} onSuccess={fetchProduct} />
    )
  }, [openPanel, productId, fetchProduct])


  if (isLoading || !productId) {
    return (
      <section className="w-full overflow-hidden border bg-white rounded-2xl p-8">
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Chargement...</p>
        </div>
      </section>
    );
  }

  if (error || !product) {
    return (
      <section className="w-full overflow-hidden border bg-white rounded-2xl p-8">
        <div className="flex items-center justify-center h-64">
          <p className="text-red-500">{error || "Produit introuvable"}</p>
        </div>
      </section>
    );
  }

  // "Anyone" is the default and says nothing about the product: only a real target is shown.
  const hasTargetedSubscriber = !!product.targetedSubscriber && product.targetedSubscriber !== TargetedSubscriberEnum.Any;
  const targetedSubscriberLabel = hasTargetedSubscriber ? t(targetedSubscriberLabels[product.targetedSubscriber]) : '';

  const headerConfig: EntityDetailsHeaderConfig = {
    icon: <Package className="text-primary" />,
    logoURL: product.logoURL,
    title: product.label,
    subtitle: product.code,
    titleAction: {
      label: 'Modifier le produit',
      icon: EditIcon,
      onClick: openUpdateProduct
    },
    infoSections: [
      {
        icon: FileText,
        label: "BRANCHE",
        value: product.branchLabel,
        disabled: true
      },
      // {
      //   icon: FileText,
      //   label: "SOUS-BRANCHE",
      //   value: product.subBranchLabel,
      //   disabled: true
      // },
      {
        icon: TrendingUp,
        label: "RECONDUITE TACITE",
        value: product.isTacitRenewal ? "Oui" : "Non",
        disabled: true
      },
      {
        icon: Layers,
        label: "FORMULE À GARANTIES FIXES",
        value: product.isFixedFormula ? "Oui" : "Non",
        disabled: true
      },
      ...(hasTargetedSubscriber ? [{
        icon: UserCheck,
        label: "SOUSCRIPTEUR VISÉ",
        value: targetedSubscriberLabel,
        disabled: true
      }] : [])
    ],
    badge: {
      label: product.ownerName ?? 'Sans propriétaire',
      variant: 'green',
      icon: Package
    },
    metadata: {
      icon: Calendar,
      text: `Créé le ${new Date(product.creationDate).toLocaleDateString('fr-FR')}`
    }
  };

  // Configuration des Stats
  const statsConfig: EntityDetailsStatConfig = {
    stats: [
      {
        label: "Code Produit",
        value: product.code
      },
      {
        label: "Branche",
        value: product.branchLabel
      },
      // {
      //   label: "Sous-branche",
      //   value: product.subBranchLabel
      // },
      {
        label: "Propriétaire",
        value: product.ownerName ?? '—'
      },
      {
        label: "Contrats actifs",
        value: product.activeContractsCount.toString()
      },
      {
        label: "Devis crées",
        value: product.quotesCount.toString()
      },
      {
        label: "Reconduite tacite",
        value: product.isTacitRenewal ? "Activée" : "Désactivée"
      },
      {
        label: "Formule à garanties fixes",
        value: product.isFixedFormula ? "Oui" : "Non"
      },
      ...(hasTargetedSubscriber ? [{
        label: "Souscripteur visé",
        value: targetedSubscriberLabel
      }] : []),
      {
        label: "Statut",
        value: product.isActive ? "Actif" : "Inactif"
      },
      {
        label: "Date de création",
        value: new Date(product.creationDate).toLocaleDateString('fr-FR')
      },
      ...(product.lastModificationDate ? [{
        label: "Dernière mise à jour",
        value: new Date(product.lastModificationDate).toLocaleDateString('fr-FR')
      }] : [])
    ]
  };

  // Pas encore d'action au clic sur un document (ouverture/téléchargement à venir).
  const handleDocumentClick = () => {};

  return (
    <section className="w-full overflow-hidden border bg-white rounded-2xl">
      <EntityDetailsHeader config={headerConfig} />
      <div className="p-8 space-y-8 shadow-inner">
        <EntityDetailsStat config={statsConfig} />

        {/* Tableau des documents */}
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-2">
            <ProductsDocumentHistory
              productId={productId}
              onDocumentClick={handleDocumentClick}
              isLoading={false}
            />
          </div>
          <div className="col-span-4">
            <ProductPlateformes productId={productId} />
          </div>
          <div className="col-span-6">
            <GenericFunnelsTable
              plateformeKeys={plateformeKeys}
              forcedFilters={{ productIds: [productId] }}
              title={`Tunnel du produit`}
              onCreateConfig={{
                label: `Ajouter un tunnel pour ce produit`,
                onClick: openAddFunnel
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};
